import { Avatar, Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import React from 'react';
import { getSystemFallbackVisual } from '../Helper/systemVisual';
import { ServiceTechnology } from 'src/DeviceIntegrationManagement/Providers/DeviceIntegrationProvider';

interface Props {
  system: ServiceTechnology;
  size?: number;
}

/**
 * The logo/icon tile for a system, shown on an integration card and in the
 * catalog. Renders the vendor's square icon mark when present, otherwise a
 * colored icon tile. The wide `logo` lockup is deliberately NOT used here — it
 * renders illegibly small in a square; it belongs on roomier surfaces.
 */
export const SystemTile = ({ system, size = 44 }: Props): React.JSX.Element => {
  if (system.logo) {
    return (
      <Avatar
        src={system.manufacturer?.mark ?? system.logo}
        alt={system.name}
        variant={'rounded'}
        sx={{
          width: size,
          height: size,
          bgcolor: 'background.default',
          border: '1px solid',
          borderColor: 'divider',
          p: 1,
          '& img': { objectFit: 'contain' },
        }}
      />
    );
  }

  const { initials, color } = getSystemFallbackVisual(system);
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: 1.5,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color,
        bgcolor: (theme) => alpha(color, theme.palette.mode === 'dark' ? 0.28 : 0.14),
        fontWeight: 800,
        fontSize: Math.round(size * (initials.length > 1 ? 0.34 : 0.42)),
        letterSpacing: initials.length > 1 ? 0.5 : 0,
        userSelect: 'none',
      }}
    >
      {initials}
    </Box>
  );
};
